/**
 * <MotifSurface> — a hero/empty surface with the shyrdak GMotif behind it.
 *
 * Bakes in the `relative isolate overflow-hidden` + negative-z layering that is
 * easy to get wrong, and FORCES an explicit faint opacity (GMotif itself
 * defaults to 0.5 — a missed override would render the motif at ~8× intensity).
 * Reuses @garden/ui GMotif; never reimplements the SVG. The motif is decorative
 * (aria-hidden, pointer-events-none) and never animates.
 *
 * Use on large hero / empty / scroll regions only — never inside cards, modals,
 * rows, or dense forms.
 */
import clsx from "clsx";
import type { ReactNode } from "react";

import { GMotif, type GMotifTone } from "@garden/ui";

export interface MotifSurfaceProps {
  /** Stroke tone for the diamonds. Default `primary`. */
  tone?: GMotifTone;
  /** Per-instance opacity — keep faint (0.04–0.08). Default 0.06. */
  opacity?: number;
  className?: string;
  children?: ReactNode;
}

export function MotifSurface({
  tone = "primary",
  opacity = 0.06,
  className,
  children,
}: MotifSurfaceProps): ReactNode {
  return (
    <div className={clsx("relative isolate overflow-hidden", className)}>
      <GMotif tone={tone} opacity={opacity} className="absolute inset-0 -z-10" />
      {children}
    </div>
  );
}
